import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ArrowDownRight, ArrowUpRight, CircleCheck, TriangleAlert } from "lucide-react";
import { authClient } from "../lib/auth";
import { usePortfolio, type Portfolio as PortfolioData } from "../lib/api";
import { useWorkspace } from "../lib/support";
import { useOrgCan } from "../lib/access";
import { delta, fmtBucket, fmtBucketHeader, fmtMoney, fmtNumber, fmtPercent, fmtRatio, FRICTION_LABEL, PLATFORM_LABEL, segmentWho } from "../lib/format";
import { StatsToolbar } from "../components/layout";
import { SummaryTiles, StatTile, metricOf, type MetricKey } from "../components/stats";
import { ChartLegend, LineChart } from "../components/charts/LineChart";
import { Sparkline } from "../components/charts/small";
import {
  Avatar,
  ButtonLink,
  Callout,
  Card,
  CardHeader,
  Delta,
  Drawing,
  Empty,
  ErrorState,
  InlineCode,
  PageHeader,
  Segmented,
  SelectField,
  Skeleton,
  SortHeader,
  StatGrid,
  Table,
  TableScroll,
  TableSkeletonRows,
  VerdictMark,
} from "../components/ui";
import { TASK_VERDICT } from "../components/contract";
import { RELEASE_VERDICT } from "../components/releases";
import { comparedWith, type ContractHeadline, type ContractVerdictKind } from "@metrickle/schema";
import { usePageTitle } from "../lib/page-title";

type Row = PortfolioData["apps"][number];
type SortKey = "name" | "contracts" | "netSuccess" | "visitors" | "visitorsDelta" | "conversionRate" | "conversions" | "revenue" | "bounceRate";

/** Worst first; apps with no held contract sort last. */
const VERDICT_RANK: Record<ContractVerdictKind, number> = { broken: 0, watch: 1, no_baseline: 2, holding: 3 };
/** An app's verdict is its worst held contract's; null when it holds none. */
const appVerdict = (r: Row): ContractHeadline | null => r.contracts[0] ?? null;

/**
 * Net task success across monitored (held) contracts: completions that still stand over attempts. Never the
 * gross rate: a refund, a cancel or a problem report within 7 days has already been taken off.
 * Null until a contract has been judged with revocations.
 */
function netOf(contracts: ContractHeadline[]) {
  const judged = contracts.flatMap((c) => (c.success ? [c.success] : []));
  const attempts = judged.reduce((t, s) => t + s.attempts, 0);
  if (!attempts) return null;
  const completed = judged.reduce((t, s) => t + s.completed, 0);
  const revoked = judged.reduce((t, s) => t + s.revoked, 0);
  const provisional = judged.reduce((t, s) => t + s.provisional, 0);
  return { attempts, completed, revoked, provisional, rate: completed / attempts, gross: (completed + revoked) / attempts };
}

const SORTERS: Record<SortKey, (r: Row) => number | string> = {
  name: (r) => r.name.toLowerCase(),
  contracts: (r) => {
    const v = appVerdict(r);
    return v ? VERDICT_RANK[v.verdict] : 9;
  },
  netSuccess: (r) => netOf(r.contracts)?.rate ?? -1,
  visitors: (r) => r.current.visitors,
  visitorsDelta: (r) => delta(r.current.visitors, r.previous.visitors) ?? Number.POSITIVE_INFINITY,
  conversionRate: (r) => r.current.conversionRate,
  conversions: (r) => r.current.conversions,
  revenue: (r) => r.current.revenue,
  bounceRate: (r) => r.current.bounceRate,
};

export function Portfolio() {
  usePageTitle("Portfolio");
  const orgs = authClient.useListOrganizations();
  const [scope, setScope] = useState("all");
  // Support view shows only the workspace being supported.
  const { id: workspaceId, support } = useWorkspace();
  // New apps go into the active workspace.
  const canAddApp = useOrgCan(workspaceId)("apps.create");
  const q = usePortfolio(support ? support.organizationId : scope === "all" ? undefined : scope);
  const [metric, setMetric] = useState<MetricKey>("visitors");
  const data = q.data;
  const workspaceCount = new Set(data?.apps.map((a) => a.organizationId)).size;

  return (
    <>
      <PageHeader
        title="Portfolio"
        subtitle={
          data?.apps.length ? `${data.apps.length} app${data.apps.length === 1 ? "" : "s"} across ${workspaceCount} workspace${workspaceCount === 1 ? "" : "s"}` : "Where every app stands"
        }
        actions={
          !support && (orgs.data?.length ?? 0) > 1 && (
            <SelectField
              aria-label="Workspace scope"
              className="w-52"
              items={[{ value: "all", label: "All workspaces" }, ...(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))]}
              value={scope}
              onChange={setScope}
            />
          )
        }
      />
      <StatsToolbar />

      {q.isError ? (
        <Card>
          <ErrorState error={q.error} />
        </Card>
      ) : data && data.apps.length === 0 ? (
        <Card>
          <Empty title="No apps yet" illustration={<Drawing name="firstData" />}>
            {canAddApp ? (
              <>
                Add a web or mobile app to start collecting growth and UX metrics.
                <div className="mt-4">
                  <ButtonLink to="/apps/new" variant="primary">
                    Add an app
                  </ButtonLink>
                </div>
              </>
            ) : (
              "Ask a workspace admin to add an app, or to give you access to one."
            )}
          </Empty>
        </Card>
      ) : (
        <div className="space-y-4">
          <ContractsCard rows={data?.apps} />
          <Card data-tour="portfolio-summary">
            <SummaryTiles current={data?.totals.current} previous={data?.totals.previous} loading={q.isPending} selected={metric} onSelect={setMetric} interval={data?.interval}>
              {data ? (
                <LineChart
                  labels={data.series.map((p) => fmtBucket(p.t, data.interval))}
                  series={[{ id: metric, label: metricOf(metric).label, color: "var(--series-1)", values: data.series.map((p) => p[metric]) }]}
                  format={metricOf(metric).fmt}
                  table={{ caption: `${metricOf(metric).label} per ${data.interval} across all apps, oldest first`, rowHeader: fmtBucketHeader(data.interval) }}
                />
              ) : (
                <Skeleton className="mx-2 h-[240px]" />
              )}
            </SummaryTiles>
          </Card>

          <div className="grid gap-4 @4xl:grid-cols-3">
            <GrowthCard data={data} />
            <MoversCard data={data} />
          </div>

          <AppsTable rows={data?.apps} loading={q.isPending} />
        </div>
      )}
    </>
  );
}

/** How many gap alerts get the full card; the rest are listed one line each below them. */
const ALERTS_SHOWN = 3;

/**
 * Can everyone finish the key tasks? Leads with what's wrong, in plain words: who is falling
 * behind, on which task, by how much and where they drop off. Without evidence yet, it says what
 * it's watching for instead. Per-app detail (net success, which apps hold) lives in the table below.
 */
export function ContractsCard({ rows }: { rows: Row[] | undefined }) {
  if (!rows) return <Skeleton className="h-28" />;
  const withContracts = rows.filter((r) => r.contracts.length > 0);
  const all = withContracts.flatMap((r) => r.contracts.map((c) => ({ r, c })));
  const failing = all
    .filter(({ c }) => c.verdict === "broken" || c.verdict === "watch")
    .sort((a, b) => VERDICT_RANK[a.c.verdict] - VERDICT_RANK[b.c.verdict] || (b.c.worst?.successGap ?? 0) - (a.c.worst?.successGap ?? 0));
  const tasks = (v: ContractVerdictKind) => all.filter(({ c }) => c.verdict === v).length;
  const holding = withContracts.filter((r) => appVerdict(r)?.verdict === "holding");
  const pending = withContracts.filter((r) => appVerdict(r)?.verdict === "no_baseline");
  return (
    <Card aria-labelledby="contracts-title">
      <CardHeader
        title={<span id="contracts-title">Can everyone finish your protected tasks?</span>}
        subtitle="People using accessibility settings, or any one browser, system or device, against everyone else, since each app's latest release or over the last week."
      />

      {withContracts.length === 0 ? (
        <p className="border-t border-line px-4 py-3 text-sm text-ink-2">
          No protected tasks yet. Protect one, like sign-up or checkout, on an app's Tasks page to see here whether everyone finishes it.
        </p>
      ) : (
        <>
          {failing.length > 0 && (
            <ul className="space-y-3 border-t border-line px-4 py-3" aria-label="Gaps">
              {failing.slice(0, ALERTS_SHOWN).map(({ r, c }) => (
                <GapAlert key={c.taskId} r={r} c={c} multiApp={withContracts.length > 1} />
              ))}
            </ul>
          )}
          {failing.length > ALERTS_SHOWN && (
            <ul className="border-t border-line" aria-label="More gaps">
              {failing.slice(ALERTS_SHOWN).map(({ r, c }) => (
                <li key={c.taskId} className="border-b border-line last:border-0">
                  <Link to={contractLink(r, c)} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2 text-sm hover:bg-surface-2">
                    <span className="w-32 shrink-0">
                      <VerdictMark map={TASK_VERDICT} verdict={c.verdict} size={14} />
                    </span>
                    <span className="min-w-0 flex-1 text-ink">
                      {c.worst ? <>{segmentWho(c.worst.key)} on {c.taskName}</> : c.taskName}
                      <span className="text-ink-3"> · {r.name}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-1 border-t border-line px-4 py-2.5">
            {failing.length === 0 &&
              (pending.length > 0 && holding.length === 0 ? (
                <p className="text-sm font-medium text-ink">
                  Too early to tell {pending.length === 1 ? `for ${pending[0]!.name}` : `for ${plural(pending.length, "app", "apps")}`}
                  <span className="font-normal text-ink-2">: we'll flag a gap as soon as there's enough to be sure.</span>
                </p>
              ) : (
                <p className="text-sm font-medium text-ink">
                  <CircleCheck size={15} aria-hidden className="mr-1.5 inline-block align-[-2px] text-good" />
                  No gaps found{pending.length > 0 && " so far"}.
                </p>
              ))}
            <p className="text-xs text-ink-2">
              {plural(all.length, "protected task", "protected tasks")} across {plural(withContracts.length, "app", "apps")}
              {tasks("broken") > 0 && <> · {plural(tasks("broken"), "gap", "gaps")}</>}
              {tasks("watch") > 0 && <> · {plural(tasks("watch"), "early signal", "early signals")}</>}
              {tasks("holding") > 0 && <> · {fmtNumber(tasks("holding"))} with no gap</>}
              {tasks("no_baseline") > 0 && <> · {fmtNumber(tasks("no_baseline"))} too early to tell</>}
            </p>
          </div>
        </>
      )}
    </Card>
  );
}

const contractLink = (r: Row, c: ContractHeadline) =>
  c.caseId ? `/apps/${r.appId}/cases?case=${encodeURIComponent(c.caseId)}` : `/apps/${r.appId}/tasks?task=${encodeURIComponent(c.taskId)}`;

/**
 * Revenue the gap cost in the window: the completions this setting fell short by, at the average
 * revenue a kept completion brought in. Null when the task carries no revenue or the gap is time only.
 */
function revenueAtStake(c: ContractHeadline): number | null {
  const w = c.worst;
  const s = c.success;
  if (!w || !s || !s.completed || s.revenue.kept <= 0 || !w.breaches.includes("success") || w.successGap <= 0) return null;
  return w.successGap * w.attempts * (s.revenue.kept / s.completed);
}

/** One broken or watched contract, said the way a person would: who, which task, how far behind, where. */
function GapAlert({ r, c, multiApp }: { r: Row; c: ContractHeadline; multiApp: boolean }) {
  const w = c.worst;
  const broken = c.verdict === "broken";
  const who = w ? segmentWho(w.key) : "Some people";
  const others = w ? comparedWith(w.key) : "everyone else";
  const slowOnly = w && !w.breaches.includes("success");
  const stake = revenueAtStake(c);
  const where = w?.hotspot ? (w.hotspot.label ? `“${w.hotspot.label}”` : w.hotspot.target) : null;
  return (
    <li>
      <Callout tone={broken ? "bad" : "warn"} icon={<TriangleAlert size={16} aria-hidden />}>
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <h3 className="text-sm font-semibold text-ink">
            {who} {broken ? "are struggling with" : "may be struggling with"} {c.taskName}
          </h3>
          <VerdictMark map={TASK_VERDICT} verdict={c.verdict} size={12} className="text-xs" />
        </div>
        {w && (
          <p className="mt-2 text-sm text-ink">
            {slowOnly ? (
              <>
                They take <b className="text-base">{w.p90Ratio != null && fmtRatio(w.p90Ratio)}</b> as long to finish it as {others}, comparing the slowest 10%.
              </>
            ) : (
              <>
                <b className="text-base">{fmtPercent(w.successRate)}</b> completed it, against <b className="text-base">{fmtPercent(w.baselineRate)}</b> of {others}.
              </>
            )}
            {stake != null && stake >= 1 && (
              <>
                {" "}
                Closing it is worth about <b>{fmtMoney(stake)}</b>
                {c.releaseVersion ? <> since {c.releaseVersion}</> : <> this week</>}.
              </>
            )}
          </p>
        )}
        {w?.step && (
          <p className="mt-1 text-sm text-ink-2">
            {w.stepShare != null && w.baselineStepShare != null ? (
              <>
                {fmtPercent(w.stepShare, 0)} stop at <InlineCode>{w.step}</InlineCode>, against {fmtPercent(w.baselineStepShare, 0)}
              </>
            ) : (
              <>
                They fall behind at <InlineCode>{w.step}</InlineCode>
              </>
            )}
            {w.hotspot && (
              <>
                . {FRICTION_LABEL[w.hotspot.name]?.label ?? w.hotspot.name}
                {where && <> on {where}</>}
              </>
            )}
            .
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="text-ink-3">
            {r.name}
            {multiApp && <> · {r.organizationName}</>}
            {c.releaseVersion && <> · since {c.releaseVersion}</>}
            {w && <> · {plural(w.attempts, "attempt", "attempts")}</>}
          </span>
          <Link to={contractLink(r, c)} className="font-medium text-accent-text hover:underline">
            {c.caseId ? "Open the case" : "Investigate"}
            <span className="sr-only">
              : {who} on {c.taskName}
            </span>{" "}
            →
          </Link>
        </div>
      </Callout>
    </li>
  );
}

const plural = (n: number, one: string, many: string) => `${fmtNumber(n)} ${n === 1 ? one : many}`;

function GrowthCard({ data }: { data: PortfolioData | undefined }) {
  const g = data?.growth;
  const total = g ? g.newVisitors + g.returningVisitors : 0;
  return (
    <Card className="@4xl:col-span-1">
      <CardHeader title="Engagement" subtitle="Active visitors as of the end of the range" />
      {g ? (
        <>
          <StatGrid cols={3}>
            <StatTile label="DAU" value={fmtNumber(g.dau)} hint="Unique visitors, last 24h" />
            <StatTile label="WAU" value={fmtNumber(g.wau)} hint="Unique visitors, last 7 days" />
            <StatTile label="MAU" value={fmtNumber(g.mau)} hint="Unique visitors, last 30 days" />
          </StatGrid>
          <div className="px-4 pt-2 pb-3">
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-ink-2">Stickiness (DAU/MAU)</span>
              <span className="font-semibold text-ink tabular">{fmtPercent(g.stickiness)}</span>
            </div>
            <div className="mt-4 flex items-baseline justify-between text-xs">
              <span className="text-ink-2">New vs returning</span>
              <span className="text-ink-3 tabular">
                {fmtNumber(g.newVisitors)} / {fmtNumber(g.returningVisitors)}
              </span>
            </div>
            <div className="mt-1.5 flex h-2 gap-0.5 overflow-hidden rounded-full" role="img" aria-label={`${fmtNumber(g.newVisitors)} new, ${fmtNumber(g.returningVisitors)} returning`}>
              <div className="rounded-l-full" style={{ width: `${total ? (g.newVisitors / total) * 100 : 50}%`, background: "var(--series-1)" }} />
              <div className="flex-1 rounded-r-full" style={{ background: "var(--series-2)" }} />
            </div>
            <div className="mt-1.5">
              <ChartLegend
                inline
                series={[
                  { id: "new", label: "New", color: "var(--series-1)" },
                  { id: "returning", label: "Returning", color: "var(--series-2)" },
                ]}
              />
            </div>
            <p className="mt-2 text-[11px] text-ink-3">
              New: first seen in this period and haven't come back yet. Returning: visited before this period, or came back after 30+ minutes away.
            </p>
          </div>
        </>
      ) : (
        <Skeleton className="m-4 h-40" />
      )}
    </Card>
  );
}

/** The apps that moved the most — where attention should go first. */
function MoversCard({ data }: { data: PortfolioData | undefined }) {
  const movers = useMemo(() => {
    const withDelta = (data?.apps ?? [])
      .filter((a) => a.previous.visitors >= 10 || a.current.visitors >= 10)
      .map((a) => ({ a, d: delta(a.current.visitors, a.previous.visitors) }))
      .filter((m): m is { a: Row; d: number } => m.d !== null && m.d !== 0);
    const up = withDelta.filter((m) => m.d > 0).sort((x, y) => y.d - x.d).slice(0, 3);
    const down = withDelta.filter((m) => m.d < 0).sort((x, y) => x.d - y.d).slice(0, 3);
    return { up, down };
  }, [data]);

  const List = ({ items, dir }: { items: { a: Row; d: number }[]; dir: "up" | "down" }) => (
    <div>
      <div className="mb-1 flex items-center gap-1 px-4 text-xs font-medium text-ink-2">
        {dir === "up" ? <ArrowUpRight size={14} aria-hidden className="text-good" /> : <ArrowDownRight size={14} aria-hidden className="text-bad" />}
        {dir === "up" ? "Growing" : "Declining"}
      </div>
      {items.length === 0 && <div className="px-4 py-2 text-xs text-ink-3">Nothing notable</div>}
      {items.map(({ a }) => (
        <Link key={a.appId} to={`/apps/${a.appId}`} className="flex items-center justify-between gap-3 px-4 py-1.5 text-sm hover:bg-surface-2">
          <span className="min-w-0 truncate text-ink">{a.name}</span>
          <span className="flex shrink-0 items-center gap-3">
            <span className="text-xs text-ink-3 tabular">{fmtNumber(a.current.visitors)}</span>
            <Delta current={a.current.visitors} previous={a.previous.visitors} className="min-w-14 justify-end" />
          </span>
        </Link>
      ))}
    </div>
  );

  return (
    <Card className="@4xl:col-span-2" data-tour="portfolio-movers">
      <CardHeader title="Biggest movers" subtitle="Change in visitors vs the previous period (apps with ≥10 visitors)" />
      {data ? (
        <div className="grid gap-4 pb-3 @xl:grid-cols-2">
          <List items={movers.up} dir="up" />
          <List items={movers.down} dir="down" />
        </div>
      ) : (
        <Skeleton className="m-4 h-32" />
      )}
    </Card>
  );
}

function AppsTable({ rows, loading }: { rows: Row[] | undefined; loading: boolean }) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "visitors", dir: -1 });
  const [group, setGroup] = useState("flat");
  const sorted = useMemo(() => {
    const fn = SORTERS[sort.key];
    return [...(rows ?? [])].sort((a, b) => {
      const x = fn(a);
      const y = fn(b);
      return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
    });
  }, [rows, sort]);

  const groups = useMemo(() => {
    if (group === "flat") return [{ name: null as string | null, rows: sorted }];
    const m = new Map<string, Row[]>();
    for (const r of sorted) m.set(r.organizationName, [...(m.get(r.organizationName) ?? []), r]);
    return [...m.entries()].map(([name, rows]) => ({ name, rows }));
  }, [sorted, group]);

  // Props for a sortable column header. A new column starts A–Z for names, largest first otherwise.
  const sortBy = (k: SortKey) => ({
    sorted: sort.key === k ? (sort.dir === 1 ? "ascending" : "descending") : null,
    onSort: () => setSort((s) => ({ key: k, dir: s.key === k ? (s.dir === 1 ? -1 : 1) : k === "name" ? 1 : -1 })),
  } as const);

  return (
    <Card data-tour="portfolio-apps">
      <CardHeader
        title="All apps"
        subtitle="Click a column to sort; conversion change is in percentage points"
        actions={<Segmented aria-label="Group apps" items={[{ value: "flat", label: "All" }, { value: "org", label: "By workspace" }]} value={group} onChange={setGroup} />}
      />
      <TableScroll label="All apps" divided>
        <Table
          caption="All apps, sortable by column. Task success is the success rate across protected tasks, after refunds, cancels and problem reports. Conversion change is in percentage points."
          busy={loading}
          className="min-w-[1200px] tabular"
        >
          <thead>
            <tr>
              <SortHeader {...sortBy("name")}>App</SortHeader>
              <th scope="col">Latest release</th>
              <SortHeader {...sortBy("contracts")}>Protected tasks</SortHeader>
              <SortHeader {...sortBy("netSuccess")} align="end">Task success</SortHeader>
              <SortHeader {...sortBy("visitors")} align="end">Visitors</SortHeader>
              <SortHeader {...sortBy("visitorsDelta")} align="end">Change</SortHeader>
              <th scope="col">Trend</th>
              <SortHeader {...sortBy("conversionRate")} align="end">Conv. rate</SortHeader>
              <SortHeader {...sortBy("conversions")} align="end">Conversions</SortHeader>
              <SortHeader {...sortBy("revenue")} align="end">Revenue</SortHeader>
              <SortHeader {...sortBy("bounceRate")} align="end">Bounce</SortHeader>
            </tr>
          </thead>
          {loading && (
            <tbody>
              <TableSkeletonRows rows={3} columns={11} />
            </tbody>
          )}
          {groups.map((g) => (
            <GroupRows key={g.name ?? "all"} name={g.name} rows={g.rows} />
          ))}
        </Table>
      </TableScroll>
    </Card>
  );
}

function GroupRows({ name, rows }: { name: string | null; rows: Row[] }) {
  return (
    // One tbody per workspace, headed by its name, so screen readers say which workspace a row is in.
    <tbody>
      {name && (
        <tr>
          <th scope="rowgroup" colSpan={11} className="bg-surface-2 py-1.5 text-xs font-medium text-ink-2">
            {name}
          </th>
        </tr>
      )}
      {rows.map((r) => {
        return (
          <tr key={r.appId} className="hover:bg-surface-2">
            <td>
              <Link to={`/apps/${r.appId}`} className="group flex min-w-0 items-center gap-2">
                <Avatar name={r.name} square />
                <span className="min-w-0">
                  <span className="block truncate font-medium text-ink group-hover:underline">{r.name}</span>
                  <span className="block truncate text-xs text-ink-3">
                    {r.organizationName} · {PLATFORM_LABEL[r.platform] ?? r.platform}
                  </span>
                </span>
              </Link>
            </td>
            <td className="text-xs whitespace-nowrap">
              <AppRelease r={r} />
            </td>
            <td className="text-xs whitespace-nowrap">
              <AppContracts r={r} />
            </td>
            <td className="text-right">
              <AppNetSuccess r={r} />
            </td>
            <td className="text-right font-medium text-ink">{fmtNumber(r.current.visitors)}</td>
            <td className="text-right">
              <Delta current={r.current.visitors} previous={r.previous.visitors} />
            </td>
            <td>
              <Sparkline values={r.spark} />
            </td>
            <td className="text-right text-ink">
              {fmtPercent(r.current.conversionRate)}
              <Delta points current={r.current.conversionRate} previous={r.previous.conversionRate} className="ml-1.5" />
            </td>
            <td className="text-right text-ink">{fmtNumber(r.current.conversions)}</td>
            <td className="text-right text-ink">{fmtMoney(r.current.revenue)}</td>
            <td className="text-right text-ink-2">{fmtPercent(r.current.bounceRate, 0)}</td>
          </tr>
        );
      })}
    </tbody>
  );
}

function AppNetSuccess({ r }: { r: Row }) {
  const net = netOf(r.contracts);
  if (!net) return <span className="text-ink-3">—</span>;
  return (
    <span title={`${fmtPercent(net.gross)} before take-backs: ${fmtNumber(net.revoked)} taken back by refunds, cancels and problem reports; ${fmtNumber(net.provisional)} still settling (under 7 days old)`}>
      <span className="font-medium text-ink">{fmtPercent(net.rate)}</span>
      {net.revoked > 0 && <span className="ml-1.5 text-xs text-ink-3">{fmtNumber(net.revoked)} taken back</span>}
    </span>
  );
}

function AppRelease({ r }: { r: Row }) {
  const rel = r.release;
  if (!rel) return <span className="text-ink-3">None in 2 weeks</span>;
  return (
    <Link to={`/apps/${r.appId}/releases/${rel.id}`} className="hover:underline">
      <span className="mr-1.5 font-mono text-ink">{rel.version}</span>
      {rel.verdict ? <VerdictMark map={RELEASE_VERDICT} verdict={rel.verdict} size={13} /> : <span className="text-ink-3">Not checked yet</span>}
    </Link>
  );
}

function AppContracts({ r }: { r: Row }) {
  const v = appVerdict(r);
  if (!v) return <span className="text-ink-3">None protected</span>;
  return (
    <Link to={contractLink(r, v)} className="hover:underline">
      <VerdictMark map={TASK_VERDICT} verdict={v.verdict} size={13} />
      <span className="ml-1 text-ink-3">
        {r.contracts.length} protected
      </span>
    </Link>
  );
}
